import { isSameDay } from 'date-fns'

// Historia 160 — cálculos y formatos puros del detalle del job. Lo que Sun NO publica calculado
// (§8.1: porcentajes, "paso i de N", "+k", nº de serie corto) se deriva aquí, en un único sitio.

export const EMPTY_VALUE = '—'

/** RN13/D10: terminadas / N en % entero TRUNCADO; 0 con N = 0. Nunca 100 sin estar todas. */
export const progressPercent = (finished = 0, total = 0) =>
  total > 0 ? Math.floor((finished * 100) / total) : 0

/** Barra acotada a 0..100. Con requerido 0 y algo consumido, llena (RN29). */
export const boundedRatio = (done = 0, total = 0) => {
  if (total <= 0) {
    return done > 0 ? 100 : 0
  }
  return Math.max(0, Math.min(100, (done * 100) / total))
}

/**
 * RN6/D17: "paso i de N" cuenta SOLO el flujo ideal. Un job de retrabajo no tiene paso.
 * @returns {{ current: number, total: number }} `current` 0 si el job no está en el flujo ideal
 */
export const idealStep = (sequence = [], jobReferenceId = '') => {
  const ideal = sequence.filter((item) => item.rework !== true)
  const index = ideal.findIndex((item) => item.jobReferenceId === jobReferenceId)
  return { current: index + 1, total: ideal.length }
}

/** RN38: el código sin el prefijo "referencia-"; si no empieza así, entero. */
export const shortSerial = (code = '', referenceCode = '') => {
  const prefix = referenceCode + '-'
  return referenceCode !== '' && code.startsWith(prefix) && code.length > prefix.length
    ? code.slice(prefix.length)
    : code
}

const decimalsOf = (value) => {
  const text = String(value)
  const dot = text.indexOf('.')
  return dot < 0 ? 0 : text.length - dot - 1
}

// Redondeo half-up en base 10 sin el error binario de `Math.round(1.005 * 100)` (= 100).
const roundHalfUp2 = (value) => Number(Math.round(Number(value + 'e2')) + 'e-2')

// Códigos de i18next de la app (`i18n.js`) → etiqueta BCP-47 para `Intl`.
const INTL_LOCALE = { eus: 'eu', pt: 'pt-PT' }
const toIntlLocale = (language = 'en') => {
  const base = String(language).split('-')[0]
  return INTL_LOCALE[base] || base
}

/**
 * RN30/G4: los decimales que tenga la cantidad, como mucho 2 con redondeo half-up, con el separador
 * del idioma. Una cantidad que llega con más de 2 decimales se pinta con 2 (1.104 → "1,10").
 */
export const formatQuantity = (value = 0, language = 'en') => {
  const number = Number(value) || 0
  const decimals = decimalsOf(number)
  const shown = decimals > 2 ? 2 : decimals
  return new Intl.NumberFormat(toIntlLocale(language), {
    minimumFractionDigits: shown,
    maximumFractionDigits: shown,
    useGrouping: false
  }).format(decimals > 2 ? roundHalfUp2(number) : number)
}

/** La cantidad con su unidad; sin unidad, sin espacio sobrante. */
/** E-7/RN55: tipos de fila de D2. Un componente pieza no tiene registro de consumo (consumed/pending null). */
export const MATERIAL_TYPE = { RAW_MATERIAL: 'RAW_MATERIAL_REFERENCE', PART: 'PART_REFERENCE' }

export const isPartComponent = (material = {}) => material.type === MATERIAL_TYPE.PART

export const formatQuantityWithUnit = (value, unit = '', language = 'en') => {
  const quantity = formatQuantity(value, language)
  return unit ? quantity + ' ' + unit : quantity
}

/**
 * RN9/G3: solo la hora si el instante es de hoy; fecha corta + hora si no; '' sin instante.
 * @param {string} instant ISO-8601 de Sun, o null
 * @param {{ formatTime: Function, formatDate: Function }} formatter `useDateFormatter()`
 * @param {Date} now
 */
export const formatSince = (instant, formatter, now = new Date()) => {
  if (!instant) {
    return ''
  }
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return isSameDay(date, now)
    ? formatter.formatTime(date)
    : formatter.formatDate(date) + ' ' + formatter.formatTime(date)
}

/** "Nombre Apellido" de un operario de D1/D6. */
export const workerFullName = (worker = {}) => [worker.name, worker.surname].filter(Boolean).join(' ')

/** Lista separada por coma (RN18/D23); "—" si vacía. */
export const joinOrEmpty = (values = []) => {
  const present = values.filter((value) => value !== '' && value !== null && value !== undefined)
  return present.length > 0 ? present.join(', ') : EMPTY_VALUE
}

/**
 * RN19/D-T4: KPI MÁQUINA. La planificada; sin planificada, la primera activa (orden de llegada, que es
 * el de "desde"). E-9 retira el "+k": el recuento lo da `activeDeviceCount` ("N en ejecución").
 * @returns {{ name: string, groups: string[], present: boolean }}
 */
export const kpiMachine = (plannedDevice, devices = []) => {
  const active = devices.filter((device) => device.active === true)
  if (plannedDevice && plannedDevice.id !== undefined) {
    return {
      name: plannedDevice.name || '',
      groups: (plannedDevice.groups || []).map((group) => group.name),
      present: true
    }
  }
  if (active.length > 0) {
    return {
      name: active[0].name || '',
      groups: (active[0].groups || []).map((group) => group.name),
      present: true
    }
  }
  return { name: '', groups: [], present: false }
}

/**
 * E-9/RN57: clave del chip de fase de una máquina activa. Ejecución y preparación tienen su rótulo corto
 * (la clave "Preparation" es la de #113); cualquier otro código activo se traduce con su propia clave de estado.
 */
const PHASE_KEY = { ACTIVE_EXECUTION: 'Execution', ACTIVE_PREPARATION: 'Preparation' }

export const phaseKey = (jobStatusCode = '') => PHASE_KEY[jobStatusCode] || jobStatusCode || ''

/** E-9/RN57: máquinas con el job activo; la planificada no activa no cuenta. */
export const activeDeviceCount = (devices = []) => devices.filter((device) => device.active === true).length

/** RN34/D-T7: la primera con acciones pendientes; si ninguna, la primera; '' sin incidencias. */
export const initialIncidence = (incidences = []) => {
  const withPending = incidences.find((incidence) => (incidence.pendingActions || 0) > 0)
  const chosen = withPending || incidences[0]
  return chosen ? chosen.uuid : ''
}

/** RN34: se conserva la elegida mientras siga abierta; si no, la preselección. */
export const keepOrInitialIncidence = (currentUuid = '', incidences = []) =>
  incidences.some((incidence) => incidence.uuid === currentUuid) ? currentUuid : initialIncidence(incidences)

/** RN39/D24: se conserva la elegida mientras siga en la rejilla; si no, la primera; '' sin piezas. */
export const keepOrFirstPart = (currentId = '', parts = []) => {
  if (currentId !== '' && parts.some((part) => part.id === currentId)) {
    return currentId
  }
  return parts.length > 0 ? parts[0].id : ''
}
